// src/lib/routeResolver.ts

export const ApiRoutes = {
  INVENTORY_PACKAGE: "inventory/package",
  // Lightweight, card-shaped package list (same query params as
  // INVENTORY_PACKAGE) — returns only the fields a package card needs.
  INVENTORY_PACKAGE_FOR_WEBSITE: "inventory/package/forwebsite",
  INVENTORY_PACKAGE_RANDOM_PHOTOS: "inventory/package/random_photos",
  INVENTORY_PACKAGE_USED_CRUISE_TYPES: "inventory/package/used_cruise_types",
  INVENTORY_PACKAGE_USED_CRUISE_SHIPS: "inventory/package/used_cruise_ships",
  INVENTORY_PACKAGE_CRUISE_REGIONS_DESTINATIONS:
    "inventory/package/cruise_regions_destinations",
  INVENTORY_PACKAGE_BY_ID: (id: string) => `inventory/package/${id}`,
  INVENTORY_TOUR: "inventory/tour",
  INVENTORY_TOUR_BY_ID: (id: string) => `inventory/tour/${id}`,
  INVENTORY_TOUR_EXPERIENCE: "inventory/tour/experience_tours_regions",
  INVENTORY_CRUISE: "inventory/cruise",
  INVENTORY_CRUISE_BY_ID: (id: string) => `inventory/cruise/${id}`,
  INVENTORY_CRUISE_BY_CRUISE_SHIP_ID: "inventory/cruise/bycruiseshipid",

  // website endpoints
  WEBSITE_VIDEO: "website/video",
  WEBSITE_TESTIMONIAL: "website/testimonial",
  WEBSITE_TESTIMONIAL_MINI: "website/testimonial/mini",
  WEBSITE_POPUP_MESSAGE: "website/popupmessage",
  WEBSITE_PRESSRELEASE: "website/pressrelease",
  WEBSITE_SPECIALOFFERS: "website/specialoffers",
  WEBSITE_CONTACTUS: "website/contactus",
  WEBSITE_CONTACTUS_CACHING: "website/contactus/caching",
  WEBSITE_CONTACT_FORM_SUBMITTED: "website/contactformsubmitted",
  WEBSITE_SOCIALMEDIALINK_CACHING: "website/socialmedialink/caching",
  WEBSITE_FOOTER_CACHING: "website/footer/caching",
  WEBSITE_ARTICLE: "website/article",
  WEBSITE_ARTICLE_MINI: "website/article/mini",
  WEBSITE_HOMEPAGESECTIONS: "website/homepagesections",
  WEBSITE_BRANDSECTION: "website/brandsection",
  WEBSITE_HOMEPAGESLIDER: "website/homepageslider",
  WEBSITE_PAGECONTAINER: "website/pagecontainer",
  WEBSITE_PAGECONTAINER_MINI_CACHING: "website/pagecontainer/mini/caching",
  WEBSITE_PAGEMODULE_CACHING: "website/pagemodule/caching",
  WEBSITE_PAGEMODULE_CONTENT: "website/pagemodule/pages-subpages-content",
  WEBSITE_INFOGRAPHICS: "website/infographics",
  WEBSITE_BROCHURE: "website/brochure",
  WEBSITE_DESTINATION_MINI_CACHING: "website/websitedestination/mini/caching",
  /** CMS-managed static files; filter with a `name` param, returns an S3 `url`. */
  WEBSITE_FILE_ALL: "website/websitefile/all",
  WEBSITE_WEBSITEPACKAGE: "website/websitepackage",
  WEBSITE_WEBSITEDESTINATION: "website/websitedestination",
  WEBSITE_WEBSITEREGION: "website/websiteregion",
  WEBSITE_PARTNERLOGO: "website/partnerlogo",
  WEBPACK_CURVY_SLIDER: "website/curvyslider",
  WEBSITE_THIRDPARTYCODE: "website/thirdpartycode",

  // foundation endpoints
  FOUNDATION_INCLUDE: "foundation/include",
  FOUNDATION_EXCLUDE: "foundation/exclude",
  FOUNDATION_NOTE: "foundation/note",
  FOUNDATION_PACKAGETHEME: "foundation/packagetheme",
  FOUNDATION_PACKAGETHEME_USED: "foundation/packagetheme/getpackagethemesused",
  FOUNDATION_CRUISETYPE: "foundation/cruisetype",
  FOUNDATION_CRUISESHIP: "foundation/cruiseship",

  FOUNDATION_REGION_WITH_PACKAGES: (companyId: string | number | undefined) =>
    `foundation/region/regionswithpackages/${companyId}`,
  ADMIN_COMPANY_CACHING: (companyId: string | number | undefined) =>
    `admin/company/${companyId}/caching`,
  ADMIN_USER_GETALL: "admin/user/getallusersbycompanyid",
  ADMIN_TRAVELNETWORK: "admin/travelnetwork",
  ADMIN_HOSTAGENCY: "admin/hostagency",
  ADMIN_TERMS: "admin/terms",

  // CRM endpoints
  CRM_IS_EMAIL_EXIST: "crm/crmclient/isemailexist",
  CRM_IS_FIELDS_EXIST: "crm/crmclient/isfieldsexist",
  CRM_IS_IATA_NAME_EXIST: "crm/crmclient/isiataandnameexist",
  CRM_CLIENT: "crm/crmclient",
  CRM_CLIENT_BY_ID: (id: string) => `crm/crmclient/${id}`,

  // Foundation - countries
  FOUNDATION_COUNTRIES_IN_REGIONS: "foundation/country/getcountriesinregions",
  /**
   * Full country record by id — the only source of `flyerDocumentUrl` (the
   * downloadable destination flyer). The `websitedestination` payload does not
   * carry it, so the destination page resolves the id from the slug first.
   */
  FOUNDATION_COUNTRY_BY_ID: (id: string) => `foundation/country/${id}`,

  // Foundation - website guests
  FOUNDATION_WEBSITE_GUEST: "foundation/websiteguest",
  FOUNDATION_WEBSITE_GUEST_SUBMIT_LEAD: "foundation/websiteguest/submit-lead",

  // Inventory - packages
  INVENTORY_PACKAGE_COUNTRIES_WITH_PACKAGE:
    "inventory/package/countries_with_package",
  INVENTORY_PACKAGE_MINI_WEBSITE: "inventory/package/miniwebsite",

  // Sales
  SALES_LEAD: "sales/lead",
};

/**
 * Which site a shared page is rendered for. The single package page is shared
 * between the main site (`/package/<slug>`) and the groups microsite
 * (`/groups/package/<slug>`); the source drives the breadcrumb, sidebar, and
 * help widget without duplicating the page.
 */
export type PackageSource = "main" | "groups";

export const AppRoutes = {
  home: () => "/",
  about: () => "/About-Us",
  contact: () => "/Contact-Us",
  /** Single package page on the main site. */
  package: (slug: string) => `/package/${slug}`,
  region: (slug: string) => `/region/${slug}`,
  destination: (slug: string) => `/destination/${slug}`,
  experiences: () => "/experiences",
  experienceRegion: (slug: string) => `/experiences/region/${slug}`,
  experienceDestination: (slug: string) => `/experiences/destination/${slug}`,
  experienceTour: (slug: string) => `/experiences/tour/${slug}`,
  tourType: (slug: string) => `/experiences/tour-types/${slug}`,
  cruises: () => `/cruises`,
  /** Cruise packages for a region (e.g. /cruises/Mediterranean). */
  cruiseRegion: (region: string) => `/cruises/${region}`,
  /** Cruise packages for a destination within a region. */
  cruiseDestination: (region: string, destination: string) =>
    `/cruises/${region}/${destination}`,
  cruiseShip: (slug: string) => `/cruise-types/ships/${slug}`,
  article: (slug: string) => `/Article/${slug}`,
  pressRelease: (slug: string) => `/PressRelease/${slug}`,
  brochure: (slug: string) => `/Brochures/${slug}`,
  mediaCoverage: (slug: string) => `/MediaCoverage/${slug}`,
  infographics: (slug: string) => `/Infographics/${slug}`,
  videos: (slug: string) => `/Videos/${slug}`,
  /**
   * Same-origin proxy that streams a destination flyer as an attachment. A
   * cross-origin `download` attribute is ignored by browsers, so linking the
   * S3 URL directly would open the file instead of downloading it.
   */
  destinationFlyerDownload: (url: string, fileName: string) =>
    `/api/destination-flyer?url=${encodeURIComponent(url)}&name=${encodeURIComponent(fileName)}`,
  search: () => "/search",
  requestQuote: () => "/request-quote",
  findAgent: () => "/Find-Agent",

  // CMS files proxied from S3 through our origin (see `lib/websiteFiles.ts`).
  sitemap: () => "/sitemap.xml",
  robots: () => "/robots.txt",
  llms: () => "/llms.txt",

  // Groups microsite (separate route group → cross-layout, link with <a>).
  // Canonical slug mirrors the legacy site (/Group-Travel-Experiences); the
  // legacy `/groups` URL and other casings are redirected here by proxy.ts.
  groups: () => "/Group-Travel-Experiences",
  /** Single package page on the groups microsite. */
  groupsPackage: (slug: string) => `/Group-Travel-Experiences/package/${slug}`,
  groupsPackages: () => "/Group-Travel-Experiences#packages-sec",
  groupsContact: () => "/Group-Travel-Experiences#contact-sec",

  // Generic / wildcard catch-alls
  dynamicSubPage: (slug: string, subSlug: string) => `/${slug}/${subSlug}`,
  dynamicPage: (slug: string) => `/${slug}`,
};

/**
 * True when an internal `href` points at the Groups microsite. The first path
 * segment is compared case-insensitively so `/Group-Travel-Experiences`,
 * `/group-travel-experiences`, and the legacy `/groups` slug all match.
 *
 * The microsite lives in its own route group (`app/(groups)`) with a separate
 * layout, so links to it must trigger a full-page reload via an `<a>` tag —
 * Next's client-side `<Link>` would try to render it inside the main layout.
 */
export function isGroupsHref(href: string): boolean {
  const firstSegment = href
    .split(/[?#]/)[0] // drop any query string / hash
    .replace(/^\/+/, "") // drop leading slash(es)
    .split("/")[0]
    .toLowerCase();
  return (
    firstSegment === "group-travel-experiences" || firstSegment === "groups"
  );
}
